import { useCallback, useMemo } from 'react'

import { useLocation } from 'react-router-dom'
import { Routes } from 'common/constants'
import { logBridgeError, sendSelectContent } from 'common/helpers'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { useScreenFinishLoadTac } from 'common/hooks/useScreenFinishLoadTac'

interface DownloadFileParams {
  source: string | Blob
  fileName?: string
}
export const downloadFile = async ({ source, fileName = 'unknown.pdf' }: DownloadFileParams) => {
  let blob: Blob
  let urlToRevoke: string | null = null
  if (typeof source === 'string') {
    const response = await fetch(source)
    if (!response.ok) {
      throw new Error(`Failed to download file. Status: ${response.status}`)
    }
    blob = await response.blob()
  } else {
    blob = source
  }
  const blobUrl = URL.createObjectURL(blob)
  urlToRevoke = blobUrl

  const anchor = document.createElement('a')
  anchor.href = blobUrl
  anchor.download = fileName
  anchor.style.display = 'none'

  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  if (urlToRevoke) {
    URL.revokeObjectURL(urlToRevoke)
  }
}

export const usePdfViewerPage = () => {
  const location = useLocation()
  const { userSelectedEntity } = useAppContext()

  const title = location.state?.title

  const url = location.state?.url

  const name = location.state?.name

  const screen = useMemo(
    () =>
      location.pathname.includes(Routes.PRIVACY_POLICY_ACCEPTANCE)
        ? Routes.PRIVACY_POLICY_ACCEPTANCE_PDF_VIEWER
        : Routes.PDF_VIEWER,
    [location.pathname],
  )

  useScreenFinishLoadTac({
    name: 'PdfViewerPage',
    screen,
    action: 'PdfViewerPageFinishLoad',
    entity: userSelectedEntity,
    shouldSend: Boolean(url),
    logLabel: 'send pdf viewer page finish tac',
  })

  const file = { url: url ?? '' }

  const onDownloadFile = useCallback(async () => {
    try {
      await sendSelectContent({
        flow: 'pdf_viewer_page',
        step: screen,
        screen_name: screen,
        element_name: 'downloadPdf',
        element_type: 'button',
        parent_name: 'PdfViewerPage',
        scenario: JSON.stringify({
          screen,
          product: userSelectedEntity,
          ref_type: 'button',
          content_action: 'touch',
        }),
      })
    } catch (error: unknown) {
      logBridgeError('send pdf viewer download tic', error)
    }

    await downloadFile({ source: url, fileName: name })
  }, [url, name, screen, userSelectedEntity])

  return {
    title: typeof title === 'string' ? title : '',
    file,
    onDownloadFile,
  }
}
